import * as React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { 
  Box,
  Button,
  DialogContent, 
  DialogTitle,
  Typography,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import Cropper, { Point } from 'react-easy-crop';
import { useState } from 'react';
import { Asset } from '../../customTypings/branding/asset';
import { cropImage } from '../../utilities/crop-utils';
import { PixelCrop, Dimensions } from '../../customTypings/branding/crop';
import { patchBrandAssets } from '../../utilities/brand-api-utils';
import store, { useAppSelector } from '../../store';

interface BrandingImageDialogProps {
  imageName: string,
  image: Asset,
  imageDim: Dimensions,
  setDialog(arg: boolean): void,
  refresh(): void
}

function BrandingImageDialog({ imageName, image, imageDim, setDialog, refresh }: BrandingImageDialogProps) {
  const dims: Dimensions = imageDim;
  if (imageName === 'background.png') {
    dims.width = 1200;
    dims.height = 2133;
  }
  const cropSize: Dimensions = dims;

  if (dims.width > dims.height && dims.width > 450) {
    const scale = 450 / dims.width;
    cropSize.width = cropSize.width * scale;
    cropSize.height = cropSize.height * scale;
  } else if (dims.height > dims.width && dims.height > 500) {
    const scale = 500 / dims.height;
    cropSize.width = cropSize.width * scale;
    cropSize.height = cropSize.height * scale;
  }

  const [newImage, setNewImage] = useState<Asset>({key: '', file: ''});
  const [loadedImage, setLoadedImage] = useState<string>();
  const [activeStep, setActiveStep] = useState<number>(0);
  const activeBrand = useAppSelector(store => store.brand.activeBrand);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<PixelCrop | null>(null);

  const onNextStep = () => {
    if (activeStep < 2) {
      if (activeStep === 1 && loadedImage && croppedAreaPixels) {
        cropImage(loadedImage, croppedAreaPixels, dims).then((resp) => {
          if (resp) {
            setNewImage({key: image.key, file: resp});
          }
        });
      }
      setActiveStep((activeStep + 1));
    } else {
      if (newImage) {
        uploadImage();
      }
      setDialog(false);
    }
  };

  const onBack = () => {
    if (activeStep > 0) {
      setActiveStep((activeStep - 1));
    } else {
      setDialog(false);
    }
  };

  const fileSelected = (e: FileList | null) => {
    if (e) {
      setLoadedImage(URL.createObjectURL(e[0]));
    }
  };

  const uploadImage = async () => {
    await patchBrandAssets(store.getState().authorization.cloudKey, activeBrand, newImage);
    refresh();
  };
  
  return (
    <Box height='100%'>
      <DialogTitle
        display='flex'
        flexDirection='row'
        justifyContent='space-between'
        alignItems='center'
        sx={{
          height: '100px',
          width: '100%'
        }}>
        <div style={{
            fontWeight: '500',
            color: '#121F35',
            fontSize: '40px'
          }}>
          Change &quot;{imageName.split('.')[0]}&quot; Image
        </div>
        <Button 
          sx={{
            borderRadius: '5px',
            width: 'auto',
            height: 'auto',
            padding: '0',
            minWidth: '0',
            '&:hover': {
              opacity: '.5',
            }
          }}
          onClick={() => {
            setDialog(false);
          }}>
          <CloseIcon
            sx={{
              padding: '0',
              fontSize: '2rem',
              color: '#121F35',
              width: '40px',
              height: '40px',
            }} />
        </Button>
      </DialogTitle>
      <DialogContent
        sx={{
          height: '600px',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
        <div style={{width: '100%'}}>
          <Stepper activeStep={activeStep}>
            <Step>
              <StepLabel>Upload New Image</StepLabel>
            </Step>
            <Step>
              <StepLabel>Crop Image</StepLabel>
            </Step>
            <Step>
              <StepLabel>Confirm</StepLabel>
            </Step>
          </Stepper>
        </div>
        {(() => {
          switch(activeStep) {
            case 0:
              return (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                  <Typography 
                    variant='h5'
                    sx={{
                      marginBottom: '20px'
                    }}>Upload Image</Typography>
                  {loadedImage ? <img width='200px' src={loadedImage} alt="" /> : <></>}
                  <input 
                    type='file'
                    accept='image/png, image/jpeg, image/jpg'
                    style={{
                      marginTop: '20px'
                    }}
                    onChange={(e) => fileSelected(e.target.files)}/>
                </div>
              );
            case 1:
              return (
                <div
                  style={{
                    height: '400px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                  <Cropper
                    restrictPosition={false}
                    image={loadedImage}
                    crop={crop}
                    cropSize={{width: cropSize.width, height: cropSize.height}}
                    zoom={zoom}
                    minZoom={-1}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={(_, croppedAreaPixels) => {
                      setCroppedAreaPixels(croppedAreaPixels);
                    }} />
                </div>
              );
            case 2:
              return (
                <div style={{width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center'}}>
                  {newImage ?
                  <div style={{width: '80%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center'}}>
                    <div  
                      style={{
                        backgroundColor: '#102941',
                        margin: '16px',
                        padding: '16px',
                        width: '250px',
                        height: '380px',
                        borderRadius: '10px'
                      }}>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          width: '100%',
                          height: '100%',
                        }}>
                        <Typography variant='h5' color='white'>Current</Typography>
                        <img
                          src={image.file} width='200px' style={{backgroundColor: 'transparent'}}/>
                        <div></div>
                      </div>
                    </div>
                    <div  
                      style={{
                        margin: '16px',
                        padding: '16px',
                        width: '250px'
                      }}>
                      <Typography align='center' variant='h5'>Are you sure you want to change this image?</Typography>
                    </div>
                    <div  
                      style={{
                        backgroundColor: '#102941',
                        margin: '16px',
                        padding: '16px',
                        width: '250px',
                        height: '380px',
                        borderRadius: '10px'
                      }}>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          width: '100%',
                          height: '100%',
                        }}>
                        <Typography variant='h5' color='white'>New</Typography>
                        <img
                          src={newImage.file} width='200px' style={{backgroundColor: 'transparent'}}/>
                        <div></div>
                      </div>
                    </div>
                  </div>:
                  <></>}
                </div>
              );
          }
        })()}
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
          <Button 
            variant='contained'
            sx={{
              backgroundColor: '#BDBDBD',
              '&: hover': {
                backgroundColor: '#FE2E26'
              }
            }}
            onClick={() => onBack()}>
            {activeStep > 0 ? 'Back' : 'Cancel'}
          </Button>
          <Button 
            variant='contained'
            disabled={!loadedImage}
            onClick={() => onNextStep()}>
            {activeStep < 2 ? 'Next' : 'Confirm'}
          </Button>
        </div>
      </DialogContent>
    </Box>
  );
}

export default BrandingImageDialog;
